import type { Object3D } from 'three'
import {
  isImportedModelBodyOccurrenceRebindingApplicableToObject,
  type ImportedModelBodyTopologyOccurrenceRebinding,
} from '../../../imported-model/topology/ImportedModelTopologyRepository'
import { areImportedBodyBindingIdsEquivalent } from '../../../imported-model/utils'

/**
 * 解析 source bodyBindingId 对应的唯一最终 occurrence。
 *
 * 该规则同时服务 hierarchy/Object3D 与阵列引用最终化，必须先按来源对象隔离共享
 * repository，再优先选择精确 ID；只有精确 ID 不存在时才使用 body 等价规则。
 *
 * @param bodyBindingId 包内或旧工作文档 body occurrence。
 * @param rebindings 本次 hierarchy/body 生成的确定性映射。
 * @param sourceObject 可选的来源 Object3D，用于隔离复用定义级 ID 的独立 occurrence。
 * @returns 唯一映射；未命中时返回 null。
 */
export function resolveProjectModelBodyOccurrenceRebinding(
  bodyBindingId: string,
  rebindings: readonly ImportedModelBodyTopologyOccurrenceRebinding[],
  sourceObject?: Object3D,
): ImportedModelBodyTopologyOccurrenceRebinding | null {
  const normalizedBodyBindingId = bodyBindingId.trim()
  if (!normalizedBodyBindingId) {
    return null
  }
  const scopedRebindings = sourceObject
    ? rebindings.filter((rebinding) => (
        isImportedModelBodyOccurrenceRebindingApplicableToObject(sourceObject, rebinding)
      ))
    : rebindings
  const exactCandidates = scopedRebindings.filter((rebinding) => (
    rebinding.sourceBodyBindingId === normalizedBodyBindingId
    || rebinding.targetBodyBindingId === normalizedBodyBindingId
  ))
  const candidates = exactCandidates.length > 0
    ? exactCandidates
    : scopedRebindings.filter((rebinding) => (
        areImportedBodyBindingIdsEquivalent(rebinding.sourceBodyBindingId, normalizedBodyBindingId)
        || areImportedBodyBindingIdsEquivalent(rebinding.targetBodyBindingId, normalizedBodyBindingId)
      ))
  const targets = new Set(candidates.map((candidate) => (
    `${candidate.targetAssemblyNodeId}\u0000${candidate.targetBodyBindingId}`
  )))
  if (targets.size > 1) {
    throw new Error(`项目模型局部替换 body occurrence 映射存在歧义：${bodyBindingId}`)
  }
  return candidates[0] ?? null
}
